import { z } from 'zod';

// ---------------------------------------------------------------------------
// Enums — mirror the API's CalendarEvent / EventReservation models.
// ---------------------------------------------------------------------------

export const EVENT_STATUSES = ['draft', 'published', 'open', 'cancelled', 'finalized'] as const;
export const EventStatusSchema = z.enum(EVENT_STATUSES);
export type EventStatus = z.infer<typeof EventStatusSchema>;

export const RESERVATION_STATUSES = ['waiting', 'done', 'cancelled'] as const;
export const ReservationStatusSchema = z.enum(RESERVATION_STATUSES);
export type ReservationStatus = z.infer<typeof ReservationStatusSchema>;

export const PAYMENT_STATUSES = ['unpaid', 'paid', 'refunded'] as const;
export const PaymentStatusSchema = z.enum(PAYMENT_STATUSES);
export type PaymentStatus = z.infer<typeof PaymentStatusSchema>;

export const LOCATIONS = ['KRIT Ploiesti', 'KRIT Brasov'] as const;
export const LocationSchema = z.enum(LOCATIONS);
export type EventLocation = z.infer<typeof LocationSchema>;

// Default tile size on the storefront calendar — presentation only.
// 'compact-always' is compact with the layout's upsizing rules opted out (a
// lone compact normally renders as a normal tile; this one never does).
export const EVENT_SIZES = ['wide', 'normal', 'compact', 'compact-always'] as const;
export const EventSizeSchema = z.enum(EVENT_SIZES);
export type EventSize = z.infer<typeof EventSizeSchema>;
export const EVENT_SIZE_LABELS: Record<EventSize, string> = {
    wide: 'Wide',
    normal: 'Normal',
    compact: 'Compact',
    'compact-always': 'Compact mereu',
};

// Sequelize serializes DECIMAL columns as strings — accept both and
// normalize to number | null.
const decimal = z.preprocess(
    (v) => (v == null || v === '' ? null : Number(v)),
    z.number().nullable(),
);

// ---------------------------------------------------------------------------
// Discounts — rules stored as JSON. The exact same shape lives on an event
// (`CalendarEvent.discounts`) and at calendar level (global, time-based rules
// via GET/PUT /events-discounts). Server semantics: each rule cuts either a
// percent of the base price or a fixed lei amount (exactly one of the two);
// rules stack additively (percents capped at 100%, price floored at 0).
// Early bird is a CONDITION composable with any kind: a rule carrying
// earlyLevel + limit applies only while its level is the current one. Levels
// are consumed in ascending order (level 2 starts when level 1's seats run
// out); rules sharing a level must declare the same seat count. An event
// that defines ANY own discounts opts out of the calendar-level rules.
// ---------------------------------------------------------------------------

export const DISCOUNT_KINDS = ['general', 'card'] as const;
export const DiscountKindSchema = z.enum(DISCOUNT_KINDS);
export type DiscountKind = z.infer<typeof DiscountKindSchema>;

export const DISCOUNT_KIND_LABELS: Record<DiscountKind, string> = {
    general: 'Reducere generala',
    card: 'Plata online cu cardul',
};

// The fixed API contract — the server validates this exact shape (and enforces
// percent-XOR-amount + the per-level seat agreement; the form schema below
// guarantees both on send).
export const DiscountRuleSchema = z.object({
    kind: DiscountKindSchema,
    // 0-100, up to 2 decimals; coerced because JSON columns can hold strings.
    percent: z.coerce.number().min(0).max(100).optional(),
    // Fixed cut in lei off the base price — the alternative to `percent`.
    amount: z.coerce.number().positive().optional(),
    // Early-bird condition — always together: the level this rule belongs to
    // and that level's seat count.
    earlyLevel: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().positive().optional(),
    // ISO datetimes bounding the rule's validity window; null/absent = open.
    from: z.string().nullish(),
    to: z.string().nullish(),
    // Calendar-level (global) rules only; null/absent = both shops.
    location: LocationSchema.nullish(),
});
export type DiscountRule = z.infer<typeof DiscountRuleSchema>;

// Rules stored before early bird became a composable condition used the
// kinds 'early' / 'early-card'. The calendar-level GET is normalized by the
// API, but event rows come back raw — map legacy kinds onto the current
// shape (each legacy rule = its own level, in array order) before parsing.
export const normalizeLegacyDiscounts = (rules: unknown): unknown => {
    if (!Array.isArray(rules)) return rules;
    const levels = { early: 0, 'early-card': 0 };
    return rules.map((rule) => {
        if (!rule || typeof rule !== 'object') return rule;
        const { kind } = rule as { kind?: string };
        if (kind === 'early') return { ...rule, kind: 'general', earlyLevel: ++levels.early };
        if (kind === 'early-card') {
            return { ...rule, kind: 'card', earlyLevel: ++levels['early-card'] };
        }
        return rule;
    });
};

// GET/PUT /events-discounts envelope.
export const CalendarDiscountsSchema = z.object({
    discounts: z.array(DiscountRuleSchema),
});

// ---------------------------------------------------------------------------
// Highlights — display-only value/label chips on an event (e.g. KOINS
// scenarios: value "2", label "participare"). Rendered on the preview image,
// the storefront and the admin; zero/empty values are hidden everywhere.
// ---------------------------------------------------------------------------

export const EventHighlightSchema = z.object({
    key: z.string().nullish(),
    label: z.string(),
    value: z.union([z.string(), z.number()]).transform(String),
    icon: z.string().nullish(),
});
export type EventHighlight = z.infer<typeof EventHighlightSchema>;

// ---------------------------------------------------------------------------
// Reservations — the events list includes a trimmed attribute set, the event
// detail includes everything; optional fields cover both shapes.
// ---------------------------------------------------------------------------

export const ReservationSchema = z.object({
    id: z.number(),
    status: ReservationStatusSchema,
    // True = queued, holds no slot; only meaningful while status === 'waiting'.
    // Defensive: older rows may omit it or serialize it as 0/1/'true'.
    waitlisted: z.preprocess(
        (v) => v === true || v === 1 || v === '1' || v === 'true',
        z.boolean(),
    ),
    paymentStatus: PaymentStatusSchema,
    confirmationCode: z.string(),
    name: z.string(),
    email: z.string(),
    phone: z.string(),
    amount: decimal.optional(),
    notes: z.string().nullish(),
    userId: z.number().nullish(),
    eventId: z.number().optional(),
    paymentTypeId: z.number().nullish(),
    paymentType: z.object({ id: z.number(), name: z.string() }).nullish(),
    createdAt: z.string().optional(),
});
export type Reservation = z.infer<typeof ReservationSchema>;

// Rows from the global `/event-reservations` list — same shape plus the
// included event summary.
export const ReservationListItemSchema = ReservationSchema.extend({
    eventId: z.number(),
    event: z
        .object({
            id: z.number(),
            title: z.string(),
            start: z.string(),
            end: z.string().optional(),
            location: z.string().optional(),
            price: decimal.optional(),
        })
        .nullish(),
});
export type ReservationListItem = z.infer<typeof ReservationListItemSchema>;

export const ReservationListSchema = z.object({
    data: z.array(ReservationListItemSchema),
    count: z.number().optional(),
});

// ---------------------------------------------------------------------------
// Consumables — `/events/:id/consumables` hydrates `.item`; the event detail
// includes `product` / `single` instead. The api layer normalizes both into
// `item` before parsing.
// ---------------------------------------------------------------------------

export const ConsumableItemSchema = z
    .object({
        id: z.number(),
        // Product fields
        title: z.string().nullish(),
        sku: z.string().nullish(),
        thumbnail: z.string().nullish(),
        // Card fields
        name: z.string().nullish(),
        number: z.union([z.string(), z.number()]).nullish(),
        image: z.string().nullish(),
        expansion: z.object({ name: z.string().nullish() }).passthrough().nullish(),
    })
    .passthrough();
export type ConsumableItem = z.infer<typeof ConsumableItemSchema>;

export const ConsumableSchema = z.object({
    id: z.number().optional(),
    eventId: z.number().optional(),
    itemType: z.enum(['product', 'single']),
    itemId: z.number(),
    quantity: z.number(),
    item: ConsumableItemSchema.nullish(),
});
export type Consumable = z.infer<typeof ConsumableSchema>;

export const ConsumableListSchema = z.object({ data: z.array(ConsumableSchema) });

// ---------------------------------------------------------------------------
// Rounds — tournament round slots set from the "Evenimente curente" board,
// stored in the event's generic `meta` JSON bucket (meta.rounds). Times are
// 'HH:MM' (24h) or '' while being filled in.
// ---------------------------------------------------------------------------

export const EventRoundSchema = z.object({
    start: z.string(),
    end: z.string(),
    // Break length (minutes) after this round; absent on rounds saved before breaks.
    breakAfter: z.number().int().nonnegative().nullish(),
});
export type EventRound = z.infer<typeof EventRoundSchema>;

export const EventMetaSchema = z
    .object({
        rounds: z.array(EventRoundSchema).nullish(),
        // Per-event defaults used to seed new rounds/breaks on the board.
        defaultRound: z.number().int().positive().nullish(),
        defaultBreak: z.number().int().nonnegative().nullish(),
        // Round start/end alert pack (1-3) assigned to this event.
        audioVersion: z.number().int().min(1).max(3).nullish(),
    })
    .passthrough();
export type EventMeta = z.infer<typeof EventMetaSchema>;

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export const CalendarEventSchema = z.object({
    id: z.number(),
    title: z.string(),
    description: z.string().nullish(),
    rules: z.string().nullish(),
    prizes: z.string().nullish(),
    start: z.string(),
    end: z.string(),
    type: z.string(),
    subtype: z.string().nullish(),
    capacity: z.number().nullish(),
    location: z.string(),
    price: decimal.optional(),
    imageLogo: z.string().nullish(),
    imageCalendarBackground: z.string().nullish(),
    imageWideBackground: z.string().nullish(),
    imageNarrowBackground: z.string().nullish(),
    // Older events (and the lean list query) may omit it — default to normal.
    size: EventSizeSchema.default('normal'),
    // Defensive: MariaDB JSON columns can leak through as raw strings.
    highlights: z.preprocess(
        (v) => {
            if (typeof v !== 'string') return v;
            try {
                return JSON.parse(v);
            } catch {
                return null;
            }
        },
        z.array(EventHighlightSchema).nullish(),
    ),
    // Defensive: MariaDB JSON columns can leak through as raw strings.
    // Event rows come back in whatever format they were saved — normalize
    // legacy early kinds before parsing.
    discounts: z.preprocess(
        (v) => {
            let parsed: unknown = v;
            if (typeof v === 'string') {
                try {
                    parsed = JSON.parse(v);
                } catch {
                    return null;
                }
            }
            return normalizeLegacyDiscounts(parsed);
        },
        z.array(DiscountRuleSchema).nullish(),
    ),
    // Defensive: MariaDB JSON columns can leak through as raw strings.
    meta: z.preprocess(
        (v) => {
            if (typeof v !== 'string') return v;
            try {
                return JSON.parse(v);
            } catch {
                return null;
            }
        },
        EventMetaSchema.nullish(),
    ),
    status: EventStatusSchema,
    slug: z.string().optional(),
    platformEventId: z.string().nullish(),
    stockOperationId: z.number().nullish(),
    // Organizer — an employee (User) of the store the location maps to.
    // Hydrated on both the detail (id/name/email) and the list (id/name).
    organizerUserId: z.number().nullish(),
    organizer: z
        .object({ id: z.number(), name: z.string(), email: z.string().nullish() })
        .nullish(),
    revision: z.number().optional(),
    reservations: z.array(ReservationSchema).optional(),
    consumables: z.array(ConsumableSchema).optional(),
});
export type CalendarEvent = z.infer<typeof CalendarEventSchema>;

export const EventListSchema = z.object({
    data: z.array(CalendarEventSchema),
    count: z.number().optional(),
});
export const EventResponseSchema = z.object({ data: CalendarEventSchema });

export const PublishMonthResultSchema = z.object({
    data: z.object({ published: z.number(), failed: z.number() }),
});

// Append-only audit trail of organizer changes (GET /events/:id/organizer-history,
// newest first). Names are denormalized server-side at change time.
export const OrganizerHistoryEntrySchema = z.object({
    id: z.number(),
    eventId: z.number(),
    fromUserId: z.number().nullish(),
    fromName: z.string().nullish(),
    toUserId: z.number().nullish(),
    toName: z.string().nullish(),
    changedByUserId: z.number().nullish(),
    changedByName: z.string().nullish(),
    createdAt: z.string(),
});
export type OrganizerHistoryEntry = z.infer<typeof OrganizerHistoryEntrySchema>;

export const OrganizerHistoryListSchema = z.object({
    data: z.array(OrganizerHistoryEntrySchema),
});

export const PaymentTypeListSchema = z.object({
    data: z.array(z.object({ id: z.number(), name: z.string() }).passthrough()),
});
export type PaymentTypeOption = z.infer<typeof PaymentTypeListSchema>['data'][number];

// ---------------------------------------------------------------------------
// Event form (RHF) — numeric fields stay strings in the form and are
// converted in the submit payload builder.
// ---------------------------------------------------------------------------

// Editor draft of a DiscountRule: numbers stay strings, valueType picks
// percent vs fixed lei, early + level/limit hold the early-bird condition,
// from/to hold datetime-local values, location '' = both shops. Converted
// to/from the API contract by discountRuleToForm / discountFormToRule in
// components/DiscountRulesEditor.tsx.
export const DISCOUNT_VALUE_TYPES = ['percent', 'amount'] as const;
export type DiscountValueType = (typeof DISCOUNT_VALUE_TYPES)[number];

export const DiscountRuleFormSchema = z
    .object({
        kind: DiscountKindSchema,
        valueType: z.enum(DISCOUNT_VALUE_TYPES),
        value: z
            .string()
            .min(1, 'Valoarea este obligatorie')
            .regex(/^\d{1,5}([.,]\d{0,2})?$/, 'Valoare invalida'),
        early: z.boolean(),
        level: z.string().regex(/^\d*$/, 'Numar intreg'),
        limit: z.string().regex(/^\d*$/, 'Numar intreg'),
        from: z.string(),
        to: z.string(),
        location: z.union([LocationSchema, z.literal('')]),
    })
    .superRefine((rule, ctx) => {
        const value = parseFloat(rule.value.replace(',', '.'));
        if (rule.value && !(value > 0)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['value'],
                message: 'Valoarea trebuie sa fie pozitiva',
            });
        } else if (rule.valueType === 'percent' && value > 100) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['value'],
                message: 'Maxim 100%',
            });
        } else if (rule.valueType === 'amount' && value > 10000) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['value'],
                message: 'Maxim 10000 lei',
            });
        }
        if (rule.early && !(parseInt(rule.level, 10) >= 1)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['level'],
                message: 'Completeaza nivelul (1, 2, ...)',
            });
        }
        if (rule.early && !(parseInt(rule.limit, 10) >= 1)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['limit'],
                message: 'Completeaza numarul de rezervari',
            });
        }
        if (rule.from && rule.to && new Date(rule.to) <= new Date(rule.from)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['to'],
                message: 'Sfarsitul trebuie sa fie dupa inceput',
            });
        }
    });
export type DiscountRuleForm = z.infer<typeof DiscountRuleFormSchema>;

// Rules referencing the same early-bird level must agree on its seat count —
// the ladder is ambiguous otherwise (the API rejects it too). Array-level
// check shared by the event form and the calendar-level dialog.
export const refineDiscountLevelSeats = (
    rows: DiscountRuleForm[],
    ctx: z.RefinementCtx,
): void => {
    const seats = new Map<string, string>();
    rows.forEach((row, index) => {
        if (!row.early || !row.level || !row.limit) return;
        const known = seats.get(row.level);
        if (known === undefined) seats.set(row.level, row.limit);
        else if (known !== row.limit) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: [index, 'limit'],
                message: `Nivelul ${row.level} are deja ${known} rezervari — foloseste acelasi numar`,
            });
        }
    });
};

export const DiscountRuleFormArraySchema = z
    .array(DiscountRuleFormSchema)
    .superRefine(refineDiscountLevelSeats);

export const EventFormSchema = z
    .object({
        title: z.string().trim().min(1, 'Titlul este obligatoriu'),
        type: z.string().min(1, 'Alege tipul evenimentului'),
        subtype: z.string().optional().or(z.literal('')),
        location: LocationSchema,
        // User id as a string ('' = no organizer) — the select's native value.
        organizerUserId: z.string(),
        start: z.string().min(1, 'Alege data si ora de inceput'),
        hasEndTime: z.boolean(),
        end: z.string().optional().or(z.literal('')),
        capacity: z
            .string()
            .regex(/^\d*$/, 'Numar intreg')
            .optional()
            .or(z.literal('')),
        price: z
            .string()
            .regex(/^\d*([.,]\d{0,2})?$/, 'Pret invalid')
            .optional()
            .or(z.literal('')),
        platformEventId: z.string().optional().or(z.literal('')),
        description: z.string().optional().or(z.literal('')),
        rules: z.string().optional().or(z.literal('')),
        prizes: z.string().optional().or(z.literal('')),
        imageLogo: z.string().nullable(),
        imageCalendarBackground: z.string().nullable(),
        imageWideBackground: z.string().nullable(),
        imageNarrowBackground: z.string().nullable(),
        size: EventSizeSchema,
        highlights: z
            .array(
                z.object({
                    key: z.string().optional().or(z.literal('')),
                    icon: z.string().nullable(),
                    value: z
                        .string()
                        .trim()
                        .min(1, 'Valoarea este obligatorie')
                        .max(40, 'Maxim 40 caractere'),
                    label: z
                        .string()
                        .trim()
                        .min(1, 'Eticheta este obligatorie')
                        .max(40, 'Maxim 40 caractere'),
                }),
            )
            .max(4, 'Maxim 4 proprietati'),
        discounts: DiscountRuleFormArraySchema,
    })
    .superRefine((values, ctx) => {
        if (values.hasEndTime) {
            if (!values.end) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['end'],
                    message: 'Alege ora de sfarsit',
                });
            } else if (values.start && new Date(values.end) <= new Date(values.start)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['end'],
                    message: 'Sfarsitul trebuie sa fie dupa inceput',
                });
            }
        }
    });
export type EventForm = z.infer<typeof EventFormSchema>;

// What the create/update endpoints accept.
export type EventPayload = {
    title: string;
    description: string;
    rules: string;
    prizes: string;
    start: string;
    end: string;
    type: string;
    subtype: string;
    capacity: number | null;
    // Any shop name, not just the two in LOCATIONS: the column is free text
    // server-side and the releases feature creates events for whatever shops
    // the Shops table returns. EventForm still narrows its own select.
    location: string;
    organizerUserId: number | null;
    price: number | null;
    imageLogo: string | null;
    imageCalendarBackground: string | null;
    imageWideBackground: string | null;
    imageNarrowBackground: string | null;
    size: EventSize;
    platformEventId: string | null;
    highlights: Array<{ key: string; label: string; value: string; icon: string | null }>;
    // [] = no own discounts (calendar-level rules apply). Always sent so a
    // PUT can clear previously saved rules.
    discounts: DiscountRule[];
};

// ---------------------------------------------------------------------------
// Bulk import/export — the admin parses the CSV client-side into rows, posts
// them to /events/bulk-import (dryRun first for the simulation, then for real),
// and renders the returned report. Row strings are validated server-side.
// ---------------------------------------------------------------------------

export type BulkImportRow = {
    _row: number; // 1-based CSV line (incl. header) for the report
    id: string;
    title: string;
    type: string;
    subtype: string;
    location: string;
    start: string; // ISO (client converts the local CSV datetime)
    end: string; // ISO or ''
    capacity: string;
    price: string;
    platformEventId: string;
    description: string;
    rules: string;
    prizes: string;
    highlights: string; // raw JSON cell, parsed server-side
    images: { imageLogo: string; imageCalendarBackground: string; imageWideBackground: string };
    replace: { imageLogo: boolean; imageCalendarBackground: boolean; imageWideBackground: boolean };
};

export type BulkImportRequest = {
    dryRun: boolean;
    events: BulkImportRow[];
    delete?: { enabled: boolean; startDate: string; endDate: string; location: EventLocation };
};

export const BulkImportReportSchema = z.object({
    dryRun: z.boolean(),
    summary: z.object({
        created: z.number(),
        updated: z.number(),
        skipped: z.number(),
        failed: z.number(),
        deleted: z.number(),
    }),
    rows: z.array(
        z.object({
            row: z.number().nullable(),
            id: z.number().nullable(),
            title: z.string(),
            action: z.enum(['create', 'update', 'skip', 'error']),
            message: z.string().optional(),
        }),
    ),
    deletions: z.array(
        z.object({
            id: z.number(),
            title: z.string(),
            start: z.string(),
            status: z.enum(['pending', 'deleted', 'skip', 'error']),
            message: z.string().optional(),
        }),
    ),
});
export type BulkImportReport = z.infer<typeof BulkImportReportSchema>;
export type BulkImportRowResult = BulkImportReport['rows'][number];
export type BulkImportDeletion = BulkImportReport['deletions'][number];

export const ReservationFormSchema = z.object({
    name: z.string().trim().min(1, 'Numele este obligatoriu'),
    email: z.string().trim().email('Email invalid'),
    phone: z.string().trim().min(1, 'Telefonul este obligatoriu'),
    paymentTypeId: z.string().optional().or(z.literal('')),
    amount: z
        .string()
        .regex(/^\d*([.,]\d{0,2})?$/, 'Suma invalida')
        .optional()
        .or(z.literal('')),
    notes: z.string().optional().or(z.literal('')),
});
export type ReservationForm = z.infer<typeof ReservationFormSchema>;
